import type { ILink } from "@components/core/Link";
import type { Media } from "@mw/shared/payload-types";
import type { EventName } from "@mw/shared/analyticsEventNames";
import type { StaticImageData } from "next/image";

import { BlockBreadcrumbsV2 } from "@/app/_blocks/BlockBreadcrumbsv2";
import { Link } from "@components/core/Link";
import { LinkTracked } from "@components/core/LinkTracked";
import { isValidLinkWithLabel } from "@ui/lib/link";
import { clsx } from "clsx";
import NextImage from "next/image";

import { Noise } from "@ui/components/core/Noise/Noise";
import { SeamlessTexture } from "@ui/components/core/SeamlessTexture/SeamlessTexture";
import { getImageProps } from "@utilities/payload";

export interface HeroSoundEffectsCategoryExploreProps {
  breadcrumbs: { href?: string; title: string }[];
  className?: string;
  cta?: ILink[];
  description?: string | null;
  descriptionTag?: "div" | "h1" | "h2" | "h3" | "h4" | "h5" | "p";
  image?: Media | StaticImageData | null;
  eventName?: EventName;
  title: string;
  titleTag?: "div" | "h1" | "h2" | "h3" | "h4" | "h5" | "p";
  /** On mobile: reduces title to 3xl and clamps description to 2 lines. Desktop is unchanged. */
  compactMobile?: boolean;
}

function isStaticImageData(image: Media | StaticImageData): image is StaticImageData {
  return !("id" in image);
}

const HeroSoundEffectsCategoryExplore = ({
  breadcrumbs,
  className,
  cta = [],
  description,
  descriptionTag: DescriptionTag = "p",
  image,
  eventName,
  title,
  titleTag: TitleTag = "h1",
  compactMobile = false,
}: HeroSoundEffectsCategoryExploreProps) => {
  const renderImage = () => {
    if (!image) return null;

    if (isStaticImageData(image)) {
      return <NextImage alt='' placeholder='blur' className='tw-size-full tw-object-cover' src={image} />;
    }

    const imageProps = getImageProps(image, { placeholder: true });
    if (!imageProps) return null;

    return <NextImage {...imageProps} className='tw-size-full tw-object-cover' sizes='240px' />;
  };

  return (
    <header className={className}>
      <div className='tw-flex tw-flex-col tw-gap-5 lg:tw-gap-8 lg:tw-flex-row lg:tw-items-start container'>
        <div className='tw-flex tw-flex-1 tw-min-w-0 tw-flex-col tw-items-start tw-gap-4 lg:tw-gap-7'>
          <BlockBreadcrumbsV2 links={breadcrumbs} />
          <div className='tw-flex tw-flex-col tw-gap-4 lg:tw-gap-7'>
            <TitleTag
              className={
                compactMobile ? "tw-type-3xl lg:tw-type-5xl tw-max-w-xl" : "tw-type-4xl lg:tw-type-5xl tw-max-w-xl"
              }
            >
              {title}
            </TitleTag>
            {description && (
              <DescriptionTag
                className={clsx("tw-type-sm lg:tw-type-base tw-max-w-xl", compactMobile && "tw-leading-[1.5]")}
              >
                {compactMobile ? (
                  <span className='tw-line-clamp-2 lg:tw-line-clamp-none'>{description}</span>
                ) : (
                  description
                )}
              </DescriptionTag>
            )}
          </div>
          {cta && cta.length > 0 && (
            <div className='tw-flex tw-w-full tw-min-w-0 tw-flex-row tw-flex-wrap tw-gap-3 lg:tw-items-center'>
              {cta.filter(isValidLinkWithLabel).map((link, index) => {
                const ctaLabel = link.label ?? "";
                return eventName ? (
                  <LinkTracked
                    {...link}
                    asButton
                    label={undefined}
                    className='tw-max-w-full tw-min-w-0 tw-overflow-hidden'
                    buttonVariants={{
                      variant: link.appearance === "primary" ? "primary" : "elevated",
                      size: "md",
                    }}
                    href={link.href}
                    key={link.label}
                    eventName={eventName}
                    eventProperties={{ label: ctaLabel, href: String(link.href), position: index }}
                  >
                    <span className='tw-block tw-max-w-full tw-min-w-0 tw-truncate'>{ctaLabel}</span>
                  </LinkTracked>
                ) : (
                  <Link
                    {...link}
                    asButton
                    label={undefined}
                    className='tw-max-w-full tw-min-w-0 tw-overflow-hidden'
                    buttonVariants={{
                      variant: link.appearance === "primary" ? "primary" : "elevated",
                      size: "md",
                    }}
                    href={link.href}
                    key={link.label}
                  >
                    <span className='tw-block tw-max-w-full tw-min-w-0 tw-truncate'>{ctaLabel}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
        {image && (
          <div className='tw-relative tw-hidden tw-aspect-square tw-overflow-hidden tw-rounded-sm lg:tw-block lg:tw-w-[240px] lg:tw-shrink-0'>
            {renderImage()}
            <SeamlessTexture className='tw-pointer-events-none' />
            <Noise className='tw-pointer-events-none tw-opacity-60' />
          </div>
        )}
      </div>
    </header>
  );
};

HeroSoundEffectsCategoryExplore.displayName = "HeroSoundEffectsCategoryExplore";

export { HeroSoundEffectsCategoryExplore };
